/**
 * Widget mode types
 */
export type WidgetMode =
  | "overlay"
  | "inline"
  | "inline-simple"
  | "redirect"
  | "new-tab";

/**
 * Content types supported by the widget
 */
export type ContentType =
  | "cd-searchsite"
  | "cd-form"
  | "cd-routing-form"
  | "cd-service"
  | "cd-request"
  | "url"
  | "url-element";

/**
 * Environment types
 */
export type Environment = "prod" | "dev" | "stage";

/**
 * Button type options
 */
export type ButtonType = "primary" | "secondary" | "link";

/**
 * Button size options
 */
export type ButtonSize = "small" | "medium" | "large";

/**
 * Complete widget configuration parsed from data attributes
 */
export interface WidgetConfig {
  /** Type of content to embed */
  contentType: ContentType;

  /** Content identifier (e.g., 'mealsonwheels' for cd-searchsite:mealsonwheels) */
  contentId: string;

  /** Display mode */
  mode: WidgetMode;

  /** Environment subdomain (dev, stage, or prod) */
  subdomain?: Environment;

  /** Additional URL parameters to append */
  parameters?: string;

  /** Modal/container width */
  width?: string;

  /** Modal/container height */
  height?: string;

  /** Whether width should adjust dynamically */
  variableWidth: boolean;

  /** Whether height should adjust dynamically */
  variableHeight: boolean;

  /** Modal title text */
  title?: string;

  /** Custom element ID to append widget to */
  elementId?: string;

  /** Whether modal can be closed by clicking overlay */
  locked: boolean;

  /** Show full URL link in overlay mode */
  showLink: boolean;

  /** Minimum height for inline mode (px) */
  minHeight: number;

  /** Border size (px) */
  borderSize: number;

  /** Border color */
  borderColor: string;

  /** Border radius */
  borderRadius: string;

  /** Button style type */
  buttonType?: ButtonType;

  /** Button size */
  buttonSize?: ButtonSize;

  /** Button background color */
  buttonColor?: string;

  /** Button background hover color */
  buttonHoverColor?: string;

  /** Button text color */
  buttonTextColor?: string;

  /** Button text hover color */
  buttonTextHoverColor?: string;

  /** Button border color */
  buttonBorderColor?: string;

  /** Button border hover color */
  buttonBorderHoverColor?: string;
}

/**
 * Partial config for updates
 */
export type PartialWidgetConfig = Partial<WidgetConfig>;

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Partial<WidgetConfig> = {
  mode: "overlay",
  locked: false,
  variableWidth: true,
  variableHeight: true,
  showLink: false,
  minHeight: 428,
  borderSize: 1,
  borderColor: "#F0F0F0",
};
